import {input} from '../input/InputManager.js';
import {actionGroups, bindingFromEvent, bindingLabel} from '../input/bindings.js';
import {localize} from '../utils/Localization';
import '../css/ControlsPanel.scss';

export class ControlsPanel {
    constructor(container, onBack) {
        this.device = 'keyboard';
        this.context = 'game';
        this.swallowedKeys = new Set();
        this.onBack = onBack;
        this.element = document.createElement('div');
        this.element.className = 'settingsmenu controls-panel';
        this.element.setAttribute('role', 'dialog');
        this.element.setAttribute('aria-modal', 'true');
        this.element.setAttribute('aria-labelledby', 'controls-title');
        this.element.hidden = true;
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'controls-backdrop';
        this.backdrop.hidden = true;
        container.appendChild(this.backdrop);
        this.element.innerHTML = `
            <div class="header"><div class="title" id="controls-title">${localize('controls_title')}</div><button type="button" class="back">${localize('levelbanner_back')}</button></div>
            <div class="controls-body">
                <div class="control-tabs device-tabs" role="tablist" aria-label="${localize('controls_device')}">
                    <button type="button" role="tab" data-device="keyboard">${localize('controls_keyboard')}</button>
                    <button type="button" role="tab" data-device="gamepad">${localize('controls_gamepad')}</button>
                </div>
                <p class="controller-status"></p>
                <div class="control-tabs context-tabs" role="tablist" aria-label="${localize('controls_keyboard_context')}">
                    <button type="button" role="tab" data-context="game">${localize('controls_game')}</button>
                    <button type="button" role="tab" data-context="editor">${localize('controls_editor')}</button>
                </div>
                <p class="controls-help"></p>
                <div class="binding-table" role="tabpanel" aria-label="${localize('controls_bindings')}"></div>
                <div class="capture-tools" hidden><button type="button" class="cancel">${localize('controls_cancel')}</button><button type="button" class="clear">${localize('controls_clear')}</button></div>
                <p class="binding-status" role="status" aria-live="polite"></p>
                <div class="controls-footer"><button type="button" class="reset-controls">${localize('controls_reset')}</button><span>${localize('controls_saved_device')}</span></div>
            </div>`;
        container.appendChild(this.element);
        this.element.querySelector('.back').onclick = () => { this.hide(); this.onBack(); };
        this.element.querySelectorAll('[data-device]').forEach(button => button.onclick = () => {
            this.cancel(); this.device = button.dataset.device; this.render(); button.focus();
        });
        this.element.querySelectorAll('[data-context]').forEach(button => button.onclick = () => {
            this.cancel(); this.context = button.dataset.context; this.render(); button.focus();
        });
        this.element.querySelector('.cancel').onclick = () => this.cancel();
        this.element.querySelector('.clear').onclick = () => this.assign(null);
        this.element.querySelector('.reset-controls').onclick = () => {
            this.cancel(); input.resetBindings(this.group); this.render(); this.status(localize('controls_restored'));
        };
        input.listeners.add(() => {
            if (this.element.hidden) return;
            this.cancel();
            if (!input.gamepad) this.device = 'keyboard';
            this.render();
        });
        this.keydown = event => {
            if (this.element.hidden) return;
            if(this.swallowedKeys.has(event.keyCode) && !this.pending) {
                event.preventDefault(); event.stopImmediatePropagation(); return;
            }
            if (this.pending) {
                event.preventDefault(); event.stopImmediatePropagation();
                this.swallowedKeys.add(event.keyCode);
                if (event.repeat || event.isComposing) return;
                if (this.device === 'gamepad') {
                    if (event.key === 'Escape') this.cancel();
                    return;
                }
                // Modifier-only bindings are captured on release, allowing chords.
                const binding = bindingFromEvent(event);
                if (binding && ![16, 17, 18].includes(binding.code)) this.assign(binding);
            } else if (event.key === 'Escape') {
                event.preventDefault(); event.stopImmediatePropagation(); this.hide(); this.onBack();
            } else if (event.key === 'Tab') {
                const buttons = [...this.element.querySelectorAll('button:not(:disabled)')].filter(button => button.offsetParent !== null);
                const index = buttons.indexOf(document.activeElement);
                if (event.shiftKey && index <= 0) { event.preventDefault(); buttons.at(-1)?.focus(); }
                else if (!event.shiftKey && index === buttons.length - 1) { event.preventDefault(); buttons[0]?.focus(); }
            }
        };
        this.keyup = event => {
            if (this.element.hidden) return;
            if(this.swallowedKeys.delete(event.keyCode)) event.preventDefault();
            if (this.pending && this.device === 'keyboard') {
                const binding = bindingFromEvent(event);
                if (binding && [16, 17, 18].includes(binding.code)) this.assign({code: binding.code});
            }
            event.stopPropagation();
            this.element.dataset.navCapture = String(Boolean(this.pending || this.swallowedKeys.size));
        };
        window.addEventListener('keydown', this.keydown, true);
        window.addEventListener('keyup', this.keyup, true);
        window.addEventListener('blur', () => {
            this.swallowedKeys.clear();
            if (!this.element.hidden) { this.cancel(); this.element.dataset.navCapture = 'false'; }
        });
    }
    get group() { return this.device === 'gamepad' ? 'gamepad' : this.context; }
    show() {
        input.setBlocked(true);
        this.element.hidden = false;
        this.backdrop.hidden = false;
        this.element.style.display = 'block';
        if (!input.gamepad) this.device = 'keyboard';
        this.render();
        this.element.querySelector('.back').focus({preventScroll: true});
        this.element.scrollTop = 0;
    }
    hide() {
        this.cancel(); this.element.hidden = true; this.element.style.display = 'none';
        this.backdrop.hidden = true;
        this.swallowedKeys.clear();
    }
    status(text) { this.element.querySelector('.binding-status').textContent = text; }
    render() {
        this.element.dataset.navCapture = String(Boolean(this.pending || this.swallowedKeys.size));
        this.element.dataset.navCaptureDevice = this.pending ? this.device : 'release';
        this.element.querySelectorAll('[data-device]').forEach(button => {
            button.setAttribute('aria-selected', String(button.dataset.device === this.device));
            button.disabled = button.dataset.device === 'gamepad' && !input.gamepad;
        });
        this.element.querySelectorAll('[data-context]').forEach(button => button.setAttribute('aria-selected', String(button.dataset.context === this.context)));
        this.element.querySelector('.context-tabs').hidden = this.device === 'gamepad';
        this.element.querySelector('.controller-status').textContent = input.gamepad
            ? localize(input.gamepad.mapping === 'standard' ? 'controls_connected' : 'controls_connected_custom', {controller: input.gamepad.id})
            : localize('controls_disconnected');
        this.element.querySelector('.controls-help').textContent = localize(this.device === 'gamepad'
            ? 'controls_help_gamepad' : this.context === 'editor' ? 'controls_help_editor' : 'controls_help_keyboard');
        const table = this.element.querySelector('.binding-table');
        table.replaceChildren();
        const heading = document.createElement('div');
        heading.className = 'binding-row binding-heading';
        heading.innerHTML = `<span>${localize('controls_action')}</span><span>${localize(this.device === 'keyboard' ? 'controls_key' : 'controls_input')}</span><span>${localize(this.device === 'keyboard' ? 'controls_alt_key' : 'controls_alt_input')}</span>`;
        table.appendChild(heading);
        for (const action of actionGroups[this.group]) {
            const row = document.createElement('div');
            row.className = 'binding-row';
            const label = document.createElement('span');
            label.textContent = localize(action.labelKey);
            row.appendChild(label);
            input.bindings.values[this.group][action.id].forEach((binding, slot) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.dataset.action = action.id;
                button.dataset.slot = slot;
                button.dataset.navKey = `${this.group}:${action.id}:${slot}`;
                const capturing = this.pending?.action === action.id && this.pending?.slot === slot;
                button.className = `key-binding${capturing ? ' capturing' : ''}${binding ? '' : ' unbound'}`;
                button.textContent = capturing ? localize('controls_press_input') : bindingLabel(binding, input.gamepad?.mapping === 'standard', id => localize(id));
                button.setAttribute('aria-label', localize(slot ? 'controls_binding_alternate' : 'controls_binding_primary', {
                    action: label.textContent, binding: binding ? button.textContent : localize('controls_unassigned'),
                }));
                button.onclick = () => this.start(action.id, slot);
                row.appendChild(button);
            });
            table.appendChild(row);
        }
        this.element.querySelector('.capture-tools').hidden = !this.pending;
    }
    start(action, slot) {
        this.cancel();
        this.pending = {action, slot};
        if (this.device === 'gamepad') input.startGamepadCapture(binding => this.assign(binding));
        this.render();
        this.focusBinding(action, slot);
        this.status(localize(this.device === 'keyboard' ? 'controls_capture_keyboard' : 'controls_capture_gamepad'));
    }
    focusBinding(action, slot) { this.element.querySelector(`[data-action="${action}"][data-slot="${slot}"]`)?.focus({preventScroll: true}); }
    cancel() {
        const pending = this.pending;
        this.pending = null; input.cancelCapture();
        if (pending) { this.render(); this.focusBinding(pending.action, pending.slot); }
        this.status('');
    }
    assign(binding) {
        if (!this.pending) return;
        const {action, slot} = this.pending;
        const displaced = input.setBinding(this.group, action, slot, binding);
        this.pending = null; input.cancelCapture(); this.render(); this.focusBinding(action, slot);
        const previous = [...new Set(displaced)].map(id => localize(actionGroups[this.group].find(action => action.id === id).labelKey));
        const status = localize(binding ? 'controls_binding_saved' : 'controls_binding_cleared');
        this.status(previous.length ? localize('controls_binding_displaced', {status, actions: previous.join(', ')}) : status);
    }
}
